import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import VerificationCodeInput from "../components/VerificationCodeInput";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getLogoMode, hostname } from "../utils";

interface MfaVerifyProps {
  tempToken: string;
  email: string;
  uid: string;
  onComplete: (token: string, uid: string) => void;
  onBack?: () => void;
}

const MfaVerify: React.FC<MfaVerifyProps> = ({
  tempToken,
  email,
  uid,
  onComplete,
  onBack,
}) => {
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const { whitelabelLogos } = useWhitelabel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mode = theme.palette.mode;

  const getLoginLogo = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const handleVerifyCode = async (code: string) => {
    setVerifying(true);
    setError("");

    try {
      const response = await axios.post(`${hostname}/auth/mfa/verify`, {
        code,
        tempToken,
      });

      if (response.data.success) {
        onComplete(response.data.customToken, response.data.uid);
      } else {
        throw new Error(response.data.error || "Invalid verification code");
      }
    } catch (err: any) {
      console.error("Error verifying MFA code:", err);
      setError(
        err.response?.data?.error || "Invalid verification code. Please try again."
      );
    } finally {
      setVerifying(false);
    }
  };

  const renderContent = () => (
    <>
      <Box
        sx={{
          p: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={getLoginLogo()}
          alt="logo"
          style={{
            objectFit: "contain",
            maxWidth: 200,
            maxHeight: 60,
            verticalAlign: "middle",
          }}
        />
      </Box>

      <Box sx={{ textAlign: "center", px: 3, pb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the verification code from your authenticator app
        </Typography>
        {email && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Signing in as <strong>{email}</strong>
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ px: 3, py: 3 }}>
        <VerificationCodeInput
          onComplete={handleVerifyCode}
          disabled={verifying}
          error={!!error}
          helperText={verifying ? "Verifying..." : ""}
        />
      </Box>

      <Box sx={{ px: 3, pb: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Open your authenticator app to view your verification code
        </Typography>
        {onBack && (
          <Button variant="text" onClick={onBack} disabled={verifying}>
            Use a different account
          </Button>
        )}
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "flex-start" : "center",
        minHeight: "100vh",
        width: "100vw",
        p: isMobile ? 2 : 0,
        pt: isMobile ? 4 : 0,
      }}
    >
      {isMobile ? (
        <Box sx={{ width: "100%", maxWidth: 450 }}>{renderContent()}</Box>
      ) : (
        <Paper sx={{ width: 450 }}>{renderContent()}</Paper>
      )}
    </Box>
  );
};

export default MfaVerify;
